/**
 * Shared client-side phone OTP sender.
 * Spec: docs/plans/sector-operations.md SO-D296, SO-D297 (R39 K7.a).
 *
 * Single swap point for outside phone OTP (approvals, booking auth, stays lost & found claims).
 */

import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type Auth,
  type ConfirmationResult,
} from 'firebase/auth'

let currentVerifier: RecaptchaVerifier | undefined

export function clearOutsidePhoneVerifier(): void {
  if (currentVerifier) {
    try {
      currentVerifier.clear()
    } catch {
      // ignore
    }
    currentVerifier = undefined
  }
}

export async function sendOutsidePhoneOtp(
  auth: Auth,
  phoneNumber: string,
  container?: HTMLElement | string,
  defaultContainerId = 'outside-recaptcha-container',
): Promise<ConfirmationResult> {
  clearOutsidePhoneVerifier()

  let parentEl: HTMLElement | null = null

  if (typeof document !== 'undefined') {
    if (typeof container === 'string') {
      parentEl = document.getElementById(container)
    } else if (container && typeof container === 'object') {
      parentEl = container as HTMLElement
    } else {
      let el = document.getElementById(defaultContainerId)
      if (!el) {
        el = document.createElement('div')
        el.id = defaultContainerId
        document.body.appendChild(el)
      }
      parentEl = el
    }
  }

  let targetEl: HTMLElement | string = defaultContainerId
  if (parentEl && typeof document !== 'undefined') {
    const child = document.createElement('div')
    parentEl.appendChild(child)
    targetEl = child
  } else if (container) {
    targetEl = container
  }

  const verifier = new RecaptchaVerifier(auth, targetEl, {
    size: 'invisible',
  })
  currentVerifier = verifier

  try {
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier)
    return confirmationResult
  } catch (error) {
    clearOutsidePhoneVerifier()
    throw error
  }
}
